import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/LandingView.vue'),
      meta: { public: true }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('@/views/SignupView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/verify-phone',
      name: 'verify-phone',
      component: () => import('@/views/VerifyPhoneView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/app',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/record',
      name: 'record',
      component: () => import('@/views/RecordView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/preview/:id',
      name: 'preview',
      component: () => import('@/views/PreviewView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/history',
      name: 'history',
      component: () => import('@/views/HistoryView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('@/views/CustomersView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/quotes',
      name: 'quotes',
      component: () => import('@/views/QuotesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/views/TeamManagementView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/join/:token',
      name: 'join',
      component: () => import('@/views/JoinTeamView.vue'),
      meta: { public: true }
    },
    {
      path: '/play/:id',
      name: 'play',
      component: () => import('@/views/PlaybackView.vue'),
      meta: { public: true } // Public for customer access
    },
    {
      path: '/quote/:id',
      name: 'quote-preview',
      component: () => import('@/views/QuotePreviewView.vue'),
      meta: { public: true } // Public for customer access
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/PrivacyPolicyView.vue'),
      meta: { public: true }
    },
    // Trade-specific landing pages
    {
      path: '/hvac',
      name: 'hvac',
      component: () => import('@/views/trades/HvacView.vue'),
      meta: { public: true }
    },
    {
      path: '/plumbing',
      name: 'plumbing',
      component: () => import('@/views/trades/PlumbingView.vue'),
      meta: { public: true }
    },
    {
      path: '/electrical',
      name: 'electrical',
      component: () => import('@/views/trades/ElectricalView.vue'),
      meta: { public: true }
    },
    {
      path: '/general-contracting',
      name: 'general-contracting',
      component: () => import('@/views/trades/GeneralContractingView.vue'),
      meta: { public: true }
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('@/views/trades/MaintenanceView.vue'),
      meta: { public: true }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Wait for auth to be initialized before checking
  // This ensures we don't redirect logged-in users during page refresh
  while (authStore.loading) {
    await new Promise(resolve => setTimeout(resolve, 50))
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)

  // Routes that require authentication
  if (requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Routes that require guest (login/signup pages)
  if (requiresGuest && authStore.isAuthenticated) {
    next('/app')
    return
  }

  // Landing page: redirect authenticated users to app
  if (to.path === '/' && authStore.isAuthenticated) {
    next('/app')
    return
  }

  // All other cases (including public routes)
  next()
})

export default router
